import axios from 'axios';
import useSWRV from 'swrv';

type UserBookmark = {
  id: number;
  /** old_key */
  user_id: number;
};
type TopicBookmark = {
  id: number;
  topic_id: number;
};
export type ProjectWithBookmarks = {
  id: number;
  name: string;
  user_bookmarks: UserBookmark[];
  topic_bookmarks: TopicBookmark[];
};
type ProjectsBookmarksListApiResponse = { projects: ProjectWithBookmarks[] };

const fetcher = (url: string) =>
  axios
    .get<ProjectsBookmarksListApiResponse>(url)
    .then(res => res.data.projects);

export const useProjectsBookmarksListWithSWRV = (
  teamId: string,
  cacheOnly = false,
) => {
  const SWRV = useSWRV<ProjectWithBookmarks[]>(
    `/api/v3/teamplan/${teamId}/projects/bookmarks/`,
    cacheOnly ? (null as any) : fetcher,
    {
      refreshInterval: 0,
    },
  );
  const { data, mutate } = SWRV;

  const mutations = {
    addUserBookmark(projectId: number, userBookmark: UserBookmark) {
      const projects: ProjectWithBookmarks[] = JSON.parse(
        JSON.stringify(data.value!),
      );

      const project = projects.find(project => project.id === projectId);
      if (!project) {
        return;
      }

      if (project.user_bookmarks.some(({ id }) => id === userBookmark.id)) {
        return;
      }

      project.user_bookmarks.push(userBookmark);

      return mutate(() => projects);
    },

    removeUserBookmark(projectId: number, userBookmarkId: number) {
      const projects: ProjectWithBookmarks[] = JSON.parse(
        JSON.stringify(data.value!),
      );

      const project = projects.find(project => project.id === projectId);
      if (!project) {
        return;
      }

      project.user_bookmarks = project.user_bookmarks.filter(
        ({ id }) => id !== userBookmarkId,
      );

      return mutate(() => projects);
    },

    addTopicBookmark(projectId: number, topicBookmark: TopicBookmark) {
      const projects: ProjectWithBookmarks[] = JSON.parse(
        JSON.stringify(data.value!),
      );

      const project = projects.find(project => project.id === projectId);
      if (!project) {
        return;
      }

      if (project.topic_bookmarks.some(({ id }) => id === topicBookmark.id)) {
        return;
      }

      project.topic_bookmarks.push(topicBookmark);

      return mutate(() => projects);
    },

    removeTopicBookmark(projectId: number, topicBookmarkId: number) {
      const projects: ProjectWithBookmarks[] = JSON.parse(
        JSON.stringify(data.value!),
      );

      const project = projects.find(project => project.id === projectId);
      if (!project) {
        return;
      }

      project.topic_bookmarks = project.topic_bookmarks.filter(
        ({ id }) => id !== topicBookmarkId,
      );

      return mutate(() => projects);
    },

    addProject(projectId: number, projectName: string) {
      const projects: ProjectWithBookmarks[] = JSON.parse(
        JSON.stringify(data.value!),
      );

      projects.unshift({
        id: projectId,
        name: projectName,
        user_bookmarks: [],
        topic_bookmarks: [],
      });

      return mutate(() => projects);
    },
  };

  return {
    mutations,
    ...SWRV,
  };
};
